// /admin/cloud: the operator allows people a trameria cloud (general or
// development) by their email, and revokes it. One row per person and kind,
// with its state; the failure as recorded (Kubernetes' reason included) is
// shown to the operator as it is.
//
// Promise:
// - Only an operator reaches it (others are sent home; admin/admin.tsx).
// - No event reaches the operator: the list is read again after allowing or
//   revoking, and when the page opens.
// - Revoking asks first, naming the person and the kind and what goes with
//   it; a refusal is said under that row, naming it.

import { useQuery } from "@tanstack/react-query";
import type {
  AdminSandboxDTO,
  SandboxProfile,
} from "@trameria/cloud-api/sandboxes-shared";
import { m } from "@trameria/cloud-i18n";
import {
  apiErrorMessage,
  apiErrorWithLead,
  Button,
  confirmAction,
  ErrorLine,
  iconOf,
  RowInput,
  SettingsBox,
  Skeleton,
  useApi,
  useFormat,
} from "@trameria/ui-core";
import { useState } from "react";
import type { AdminItem } from "../admin/admin";
import { adminCloudQuery, useAllowSandbox, useRevokeSandbox } from "./queries";
import { errorDetail, errorWords, phaseLabel, phaseOf } from "./words";

const CELL = "min-w-0 truncate text-[12.5px]";
/** Read to the end (the address, the state): wrapped, not cut. */
const CELL_WRAP = "min-w-0 text-[12.5px] [overflow-wrap:anywhere]";
const SUB = "min-w-0 text-[11.5px] leading-[1.6] text-faint";
/** Person / kind / state / allowed / revoke. Times and failures go below. */
const WIDE_COLUMNS =
  "@min-[600px]:grid-cols-[minmax(0,1fr)_64px_168px_88px_76px]";
const ROW_GRID = `grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-[10px] gap-y-0.5 ${WIDE_COLUMNS}`;

const kindLabel = (p: SandboxProfile) =>
  p === "dev" ? m.sandboxes_admin_kind_dev() : m.sandboxes_admin_kind_general();

/** The operator's trameria cloud, an item of /admin. */
export const cloudAdminItem: AdminItem = {
  id: "cloud",
  order: 20,
  label: () => m.sandboxes_admin_title(),
  icon: iconOf("agent"),
  description: () => m.sandboxes_admin_description(),
  wide: true,
  load: (ctx) =>
    ctx.queryClient
      .ensureQueryData(adminCloudQuery(ctx.api))
      .catch(() => undefined),
  Component: AdminCloud,
};

function AdminCloud() {
  const api = useApi();
  const rows = useQuery(adminCloudQuery(api));
  const allow = useAllowSandbox();
  const [email, setEmail] = useState("");
  const submit = (profile: SandboxProfile) => {
    const value = email.trim();
    if (value === "" || allow.isPending) return;
    allow.mutate({ email: value, profile }, { onSuccess: () => setEmail("") });
  };
  const disabled = allow.isPending || email.trim() === "";
  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit("general");
        }}
      >
        <SettingsBox>
          {/* The field and two buttons do not fit the row's right column:
              they go under the name, the whole width (the old web's). */}
          <div className="px-4 py-3">
            <div className="text-[13px] leading-[1.5] text-fg">
              {m.sandboxes_admin_allow_by_email()}
            </div>
            <div className="mt-px text-[11.5px] leading-[1.5] text-faint">
              {m.sandboxes_admin_allow_by_email_note()}
            </div>
            <div className="mt-2.5 flex flex-wrap items-center gap-2">
              <RowInput
                type="email"
                aria-label={m.sandboxes_admin_email_label()}
                // i18n-ignore: an example address
                placeholder="name@example.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (allow.isError) allow.reset();
                }}
                // On a phone the field takes its own line, the buttons under it.
                className="min-w-[240px] flex-1"
              />
              <Button type="submit" disabled={disabled}>
                {m.sandboxes_admin_allow()}
              </Button>
              <Button disabled={disabled} onClick={() => submit("dev")}>
                {m.sandboxes_admin_allow_dev()}
              </Button>
            </div>
            {allow.isError && (
              <ErrorLine>
                {apiErrorMessage(allow.error, m.sandboxes_admin_allow_failed())}
              </ErrorLine>
            )}
          </div>
        </SettingsBox>
      </form>

      {rows.isError && !rows.data ? (
        <div className="mt-6 flex items-center gap-3 text-[13px] text-muted">
          {m.web_load_failed()}
          <Button onClick={() => void rows.refetch()}>{m.web_retry()}</Button>
        </div>
      ) : !rows.data ? (
        <div className="mt-6">
          <Skeleton lines={[70, 55, 62]} />
        </div>
      ) : rows.data.items.length === 0 ? (
        <p className="mt-6 text-[13px] text-muted">
          {m.sandboxes_admin_empty()}
        </p>
      ) : (
        <div className="@container mt-6">
          {/* The budget is what decides waiting (each kind's limit, summed),
              not the memory the pods use now. */}
          <p className="px-[10px] pb-3 text-[12px] text-body tabular-nums">
            {m.sandboxes_admin_memory_usage({
              used: rows.data.memory.usedGi,
              budget: rows.data.memory.budgetGi,
            })}
            <span className="text-faint">
              {m.sandboxes_admin_memory_note()}
            </span>
          </p>
          <div
            className={`hidden items-center gap-x-[10px] px-[10px] pb-2 text-[11px] text-muted @min-[600px]:grid ${WIDE_COLUMNS}`}
          >
            <span className={CELL}>{m.sandboxes_admin_col_email()}</span>
            <span className={CELL}>{m.sandboxes_admin_col_kind()}</span>
            <span className={CELL}>{m.sandboxes_admin_col_state()}</span>
            <span className={CELL}>{m.sandboxes_admin_col_allowed()}</span>
            <span />
          </div>
          {rows.data.items.map((row) => (
            <CloudRow key={`${row.userId}:${row.profile}`} row={row} />
          ))}
        </div>
      )}
    </>
  );
}

function CloudRow({ row }: { row: AdminSandboxDTO }) {
  const format = useFormat();
  // One per row: its failure stays under it.
  const revoke = useRevokeSandbox();
  const revoked = row.revokedAt !== null;
  const who = row.email ?? row.userId;
  const label = row.name ? `${who}(${row.name})` : who;
  const made = row.deviceId !== null || row.status !== "absent";
  // The operator has no device to read; a made sandbox counts as reported.
  const state = made
    ? phaseLabel(phaseOf(row, true), null)
    : m.sandboxes_admin_not_created();
  const sub =
    made && row.lastActiveAt
      ? m.sandboxes_admin_last_active({
          time: format.dateTime(row.lastActiveAt),
        })
      : null;
  return (
    <div
      data-row={`cloud:${row.userId}:${row.profile}`}
      className="rounded-[7px] px-[10px] py-[7px] hover:bg-hover"
    >
      {/* Narrow (a phone): the address and "Revoke" on the first line, the
          kind, state and date under it; the columns were too narrow to read. */}
      <div className={ROW_GRID}>
        <span className={`${CELL_WRAP} text-body`}>
          {who}
          {row.name && <span className="text-faint"> {row.name}</span>}
        </span>
        <span className="row-start-2 flex min-w-0 flex-wrap gap-x-3 @min-[600px]:contents">
          <span className={`${CELL} text-body`}>{kindLabel(row.profile)}</span>
          <span className={`${CELL_WRAP} text-body`}>
            {revoked ? m.sandboxes_admin_revoking_state({ state }) : state}
          </span>
          <span className={`${CELL} text-body tabular-nums`}>
            {format.day(row.allowedAt)}
          </span>
        </span>
        <span className="col-start-2 row-start-1 flex justify-end @min-[600px]:col-start-auto @min-[600px]:row-start-auto">
          {!revoked && (
            <button
              type="button"
              aria-label={
                row.profile === "dev"
                  ? m.sandboxes_admin_revoke_label_dev({ email: who })
                  : m.sandboxes_admin_revoke_label_general({ email: who })
              }
              disabled={revoke.isPending}
              onClick={async () => {
                revoke.reset();
                const ok = await confirmAction({
                  title:
                    row.profile === "dev"
                      ? m.sandboxes_admin_revoke_label_dev({ email: who })
                      : m.sandboxes_admin_revoke_label_general({ email: who }),
                  body:
                    row.profile === "dev"
                      ? m.sandboxes_admin_revoke_confirm_dev({ label })
                      : m.sandboxes_admin_revoke_confirm_general({ label }),
                  action: m.sandboxes_admin_revoke(),
                  danger: true,
                });
                if (ok)
                  revoke.mutate({ userId: row.userId, profile: row.profile });
              }}
              className="box-border inline-flex h-[26px] shrink-0 cursor-pointer items-center whitespace-nowrap rounded-[13px] px-2 pb-px text-[12px] text-muted hover:bg-hover hover:text-body disabled:cursor-default disabled:opacity-50"
            >
              {m.sandboxes_admin_revoke()}
            </button>
          )}
        </span>
      </div>
      {sub && <div className={`${SUB} tabular-nums`}>{sub}</div>}
      {row.lastError && (
        <div className={`${SUB} select-text break-all text-danger`}>
          {errorWords(row.lastError)}
          {" — "}
          <span className="font-mono">{errorDetail(row.lastError)}</span>
        </div>
      )}
      {revoke.isError && (
        <ErrorLine>
          {apiErrorWithLead(
            revoke.error,
            row.profile === "dev"
              ? m.sandboxes_admin_revoke_failed_dev({ email: who })
              : m.sandboxes_admin_revoke_failed_general({ email: who }),
          )}
        </ErrorLine>
      )}
    </div>
  );
}
